import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import { exportBdcDashboardStores, getBdcDashboardStores } from "../../../../services/bdcDashboard";
import { BdcDashboardStores, BdcDashboardStoresFilter, BdcLapsedLevel } from "../../../../types/bdcDashboard.type";
import { useDebounce } from "../../../../utils/hooks/useDebounce";
import { todayBkk } from "../../format";
import { CARD_SX } from "../../styles";
import { useBdcDcs } from "../../useBdcDcs";
import { useBlobExport } from "../../useBlobExport";
import { STORE_CELLS, STORE_PAGE_SIZES } from "../constants";
import { ALL, BranchSelect, DateField, DcSelect, dcsForBranch } from "../Filters";
import { branchLabel, dashboardErrorMessage } from "../format";
import { useBdcBranches } from "../useBdcBranches";
import StoresView from "./StoresView";

/** A whole number in [min, max], or undefined (blank or out of range → the server default). */
export const parseWhole = (text: string, min: number, max: number): number | undefined => {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const value = Number(trimmed);
  return value >= min && value <= max ? value : undefined;
};

const LAPSED_LEVELS: readonly BdcLapsedLevel[] = ["late", "missed", "gone"];

const levelOf = (value: string | null): BdcLapsedLevel | undefined =>
  LAPSED_LEVELS.find((level) => level === value);

/**
 * BDC · ติดตามการสั่งของร้าน — stores that fell behind their own ordering habit, for sales /
 * marketing to visit. Date, DC, branch and the level tile live in the URL (board 1's "หลุดรอบ"
 * link lands here with them); the free-text filters stay local.
 */
export default function BdcDashboardStoresScreen() {
  const [params, setParams] = useSearchParams();
  const { dcs } = useBdcDcs();
  const branches = useBdcBranches();
  const { busy, run } = useBlobExport();

  const date = params.get("date") || todayBkk();
  const dcCode = params.get("dc_code") || ALL;
  const companyId = params.get("company_id") || ALL;
  const level = levelOf(params.get("level"));

  const [lineText, setLineText] = useState("");
  const [search, setSearch] = useState("");
  const lineNo = useDebounce(parseWhole(lineText, 0, 9999), 400);
  const q = useDebounce(search.trim(), 400);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(STORE_PAGE_SIZES[0]);

  const filter = useMemo<BdcDashboardStoresFilter>(
    () => ({
      date,
      cells: STORE_CELLS,
      ...(level ? { level } : {}),
      ...(dcCode !== ALL ? { dc_code: dcCode } : {}),
      ...(lineNo !== undefined ? { line_no: lineNo } : {}),
      ...(q ? { q } : {}),
      ...(companyId !== ALL ? { company_id: companyId } : {}),
    }),
    [date, level, dcCode, lineNo, q, companyId],
  );

  useEffect(() => {
    setPage(0);
  }, [filter, limit]);

  const [data, setData] = useState<BdcDashboardStores | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { data: body },
      } = await getBdcDashboardStores({ ...filter, page: page + 1, limit });
      setData(body);
    } catch (err) {
      setData(null);
      setError(dashboardErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filter, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === ALL) next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const showBranch = branches.length > 1;
  const branchOf = showBranch
    ? (id: string) => branchLabel(branches.find((b) => b.company_id === id), id)
    : undefined;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            BDC · ติดตามการสั่งของร้าน
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ร้านที่หลุดจากรอบสั่งปกติของตัวเอง ณ วันที่เลือก · ร้านที่ตั้ง "พักร้าน / ปิดร้าน" ไม่นับ
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            loading={busy === "stores"}
            disabled={!data || data.count === 0}
            onClick={() =>
              run("stores", () => exportBdcDashboardStores(filter), `bdc-lapsed-stores-${date.replace(/-/g, "")}.xlsx`)
            }
          >
            Export ร้านที่ต้องติดตาม
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            loading={busy === "all-stores"}
            disabled={!data}
            title="ร้านทุกร้านตามตัวกรอง DC / สาย / ค้นหา รวมร้านที่สั่งปกติ (ไม่กรองระดับ)"
            onClick={() =>
              run(
                "all-stores",
                () => exportBdcDashboardStores(filter, "all"),
                `bdc-stores-all-${date.replace(/-/g, "")}.xlsx`,
              )
            }
          >
            Export ร้านทั้งหมด
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ ...CARD_SX, p: 2, mb: 1.75 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
          <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="flex-start">
            <DateField label="ถึงวันที่" value={date} onChange={(value) => update({ date: value })} />
            <BranchSelect branches={branches} value={companyId} onChange={(value) => update({ company_id: value })} />
            <DcSelect
              dcs={dcsForBranch(dcs, branches, companyId)}
              value={dcCode}
              onChange={(value) => update({ dc_code: value })}
            />
            <TextField
              size="small"
              label="สาย"
              value={lineText}
              onChange={(e) => setLineText(e.target.value)}
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
              sx={{ width: 100 }}
            />
            <TextField
              size="small"
              placeholder="ค้นหารหัส / ชื่อร้าน"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                htmlInput: { maxLength: 100 },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ minWidth: 240 }}
            />
          </Stack>
        </LocalizationProvider>
      </Card>

      {loading && <LinearProgress sx={{ mb: 1.5 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      {data && (
        <StoresView
          data={data}
          branchOf={branchOf}
          level={level}
          onLevel={(next) => update({ level: next ?? ALL })}
          pagination={
            <TablePagination
              component="div"
              count={data.count}
              page={page}
              rowsPerPage={limit}
              rowsPerPageOptions={STORE_PAGE_SIZES}
              onPageChange={(_, next) => setPage(next)}
              onRowsPerPageChange={(e) => setLimit(Number(e.target.value))}
              labelRowsPerPage="แถวต่อหน้า"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} จาก ${count}`}
            />
          }
        />
      )}
    </Box>
  );
}
